import React, { useContext, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { OrdersDesktopTable } from "@stjeans/shared-frontend/orders"
import AppContext from "../../../AppContext.jsx"
import dbClient from "../../../db/client.js"
import auth from "../../../db/auth.js"
import { formatStatus } from "../../../helpers.js"

function mapOfflineOrderForList(order, locations) {
    const shipTo = order?.ship_to ?? null
    const billTo = order?.bill_to ?? null
    const billSameAsShip = Boolean(order?.bill_same_as_ship)
    const firstName = billSameAsShip ? shipTo?.first_name : billTo?.first_name
    const lastName = billSameAsShip ? shipTo?.last_name : billTo?.last_name
    const locationName = (locations ?? []).find((x) => x?.id === order?.location_id)?.name ?? ""

    return {
        ...order,
        location: locationName,
        master: order?.master ?? "",
        status: formatStatus(
            (order?.status == null || String(order?.status).trim() === "") ? "new" : order.status
        ),
        firstName: firstName ?? "",
        lastName: lastName ?? "",
        fishTag: order?.fish_tag ?? "",
        tripCode: order?.trip_code ?? "",
        orderDate: order?.ordered_at?.split?.("T")?.[0] ?? "",
    }
}

export default function OfflineOrdersSection({ onSelectionChange }) {
    const { app } = useContext(AppContext)
    const navigate = useNavigate()
    const [offlineOrders, setOfflineOrders] = useState(/** @type {any[]} */([]))
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        let cancelled = false
        async function load() {
            try {
                const locationId = await auth.getLocationIdAsync()
                if (!locationId) {
                    if (!cancelled) {
                        setOfflineOrders([])
                    }
                    return
                }

                const db = await dbClient.open()
                const indexName = "location_id, created_at"
                const index = db.transaction("offline_orders").store.index(indexName)
                const range = IDBKeyRange.bound([locationId, ""], [locationId, "\uffff"])
                const items = await index.getAll(range)
                if (!cancelled) {
                    setOfflineOrders(Array.isArray(items) ? items : [])
                }
            } catch (e) {
                console.error("Failed to load offline orders:", e)
                if (!cancelled) {
                    setOfflineOrders([])
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false)
                }
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [app?.locationId])

    const rows = useMemo(() => {
        const locations = app?.locations ?? []
        return offlineOrders.map((o) => mapOfflineOrderForList(o, locations))
    }, [offlineOrders, app?.locations])

    // Avoid a "flash" on refresh: don't render the section while we're still
    // checking IndexedDB and we don't yet have any offline orders to show.
    if ((isLoading && rows.length === 0) || (!isLoading && rows.length === 0)) {
        return null
    }

    return (
        <div className="mb-5 bg-warning-subtle border border-warning-subtle rounded p-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="text-uppercase text-secondary fw-semibold" style={{ fontSize: "0.875rem" }}>
                    Offline only &mdash; Waiting to sync
                </div>
                <div className="text-secondary" style={{ fontSize: "0.875rem" }}>
                    {rows.length} order{rows.length === 1 ? "" : "s"}
                </div>
            </div>
            <div className="d-none d-lg-block">
                <OrdersDesktopTable
                    orders={rows}
                    isLoadingInitial={isLoading}
                    totalRows={rows.length}
                    showPagination={false}
                    enableSelection={true}
                    onSelectionChange={onSelectionChange}
                    onOrderClick={(row) => {
                        const raw = row?._raw ?? row
                        if (!raw?.id) {
                            return
                        }
                        navigate(`/v2/orders/${raw.id}/order`, { state: { order: raw } })
                    }}
                    columns={[
                    { key: "master", label: "Master", sortable: false, preferredWidth: 105, cellType: "text", value: (o) => o.master, cellClassName: "fw-semibold", cellStyle: { color: "#0f6170" } },
                    { key: "status", label: "Status", sortable: false, minWidth: 150, preferredWidth: 150, cellType: "status", value: (o) => o.status },
                    { key: "firstName", label: "First Name", sortable: false, preferredWidth: 120, cellType: "text", value: (o) => o.firstName },
                    { key: "lastName", label: "Last Name", sortable: false, preferredWidth: 140, cellType: "text", value: (o) => o.lastName },
                    { key: "fishTag", label: "Fish Tag", sortable: false, preferredWidth: 120, cellType: "text", value: (o) => o.fishTag },
                    { key: "tripCode", label: "Trip Code", sortable: false, preferredWidth: 120, cellType: "text", value: (o) => o.tripCode },
                    { key: "orderDate", label: "Order Date", sortable: false, preferredWidth: 90, cellType: "text", value: (o) => o.orderDate },
                ]}
                />
            </div>
            <div className="d-lg-none v2-mobile-list v2-mobile-list--nested">
                {rows.map((order) => (
                    <button
                        key={order.id ?? order.master}
                        type="button"
                        className="btn btn-link w-100 text-start text-decoration-none px-0 py-2 border-bottom"
                        onClick={() => {
                            const raw = order?._raw ?? order
                            if (!raw?.id) {
                                return
                            }
                            navigate(`/v2/orders/${raw.id}/order`, { state: { order: raw } })
                        }}
                    >
                        <div className="fw-semibold text-truncate" style={{ color: "#0f6170" }}>
                            {order.master}
                        </div>
                        <div className="text-secondary text-truncate" style={{ fontSize: "0.875rem" }}>
                            {order.firstName} {order.lastName}
                            {order.fishTag ? ` • ${order.fishTag}` : ""}
                        </div>
                    </button>
                ))}
            </div>
        </div>
    )
}

